import React, { FC, useState } from 'react';

// Components
import { Layout } from '../../components/Layout';
import { Button } from '../../components/Button';
import SummarySection from '../../components/Checkout/SummarySection';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Heading } from '../../components/Heading';
import { InputCheckbox } from '../../components/InputCheckbox';

// Context
import { setFormScene } from '../../context/form-context/actions';
import { useForm } from '../../context/form-context/hooks';

// Hooks
import useSummaryAnswers from '../../hooks/useSummaryAnswers';

// Interfaces
import { Summary } from '../../interfaces/Summary';

// i18n
import { useTranslation } from '../../translations/useTranslation';
import { TranslationKey } from '../../translations';

interface Props {
  isLoading: boolean;
  onSubmit: () => Promise<void>;
  submissionErrorKey: TranslationKey | null;
}

export const CheckoutView: FC<Props> = ({
  isLoading,
  submissionErrorKey,
  onSubmit,
}) => {
  const summary = useSummaryAnswers();
  const { dispatch } = useForm();
  const { t, m, keys } = useTranslation();
  const [consent, setConsent] = useState<boolean>(false); // consent for leaving correct information
  const [consentError, setConsentError] = useState<string | undefined>();
  const [isValid, setIsValid] = useState<boolean>(false);
  const handleOnBack = () => {
    if (dispatch) {
      dispatch(setFormScene('DETAILS'));
    }
  };
  const handleOnConsentChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { checked } = event.target;
    setConsent(checked);
    if (consentError) {
      setConsentError(undefined);
    }
  };
  const validateConsent = () => {
    return new Promise<boolean>((resolve, reject) => {
      if (consent) {
        setConsentError(undefined);
        setIsValid(true);
        resolve(true);
      } else if (!consent) {
        setConsentError(t(keys.errorConsentRequired));
        setIsValid(false);
        reject(false);
      }
      return reject(false);
    });
  };
  const handleOnSubmit = () => {
    Promise.all([validateConsent()])
      .then(() => onSubmit())
      .catch(() => {});
  };

  return (
    <Layout viewId={'checkout'}>
      <Heading className="membership-app__checkout-header" size={'large'}>
        {t(keys.checkoutSummaryHeader)}
      </Heading>
      <p className="membership-app__checkout-preamble">
        {t(keys.checkoutSummarySubHeader)}
      </p>
      {summary.map((section: Summary) => (
        <SummarySection key={section.title} {...section} />
      ))}
      <div className="membership-app__consent">
        <InputCheckbox
          name={'correct-information'}
          value={'correct'}
          checked={consent}
          errorMessage={consentError}
          onChange={handleOnConsentChange}
        >
          {t(keys.submissionConsent)}
        </InputCheckbox>
      </div>
      <Button
        isLoading={isLoading}
        onClick={handleOnSubmit}
        title={t(keys.uiSubmit)}
      />
      {submissionErrorKey && (
        <div className="membership-app__checkout-error">
          <ErrorMessage message={t(submissionErrorKey)} />
        </div>
      )}
      <div
        className={`membership-app__step-back ${
          isLoading ? 'membership-app__disabled-link' : ''
        }`}
      >
        <a href="#0" onClick={handleOnBack} tabIndex={0}>
          {t(keys.uiButtonGoBack)}
        </a>
      </div>
    </Layout>
  );
};
